<template>
  <v-container fluid>
    <base-material-card color="primary" icon="mdi-calculator" inline class="ml-0 px-5 py-4">
      <template v-slot:after-heading>
        <div class="display-2 font-weight-light">Taxa Média de Depreciação</div>
      </template>
      <v-btn
        id="btn-nova-taxa-media-depreciacao"
        color="success"
        dark
        elevation="1"
        class="mt-5"
        style="float: left"
        relative
        text
        medium
        @click="$router.push('edit')"
      >
        <v-icon left size="30px">mdi-plus-circle</v-icon>
        Nova Taxa
      </v-btn>
      <v-text-field
        id="taxa-media-depreciacao-search"
        v-model="search"
        append-icon="mdi-magnify"
        class="break-search ml-auto mt-4"
        label="Procurar"
        hide-details
        single-line
        style="max-width: 250px"
      />
      <general-progress-bars class="mt-11" :items="downloadProgressData" prefix-text="Baixando Taxa Média de Depreciação" />
      <v-divider class="mt-10" />
      <v-data-table
        :headers="headers"
        :items="items"
        :search.sync="search"
        :header-props="{
          sortByText: 'Ordenar Por'
        }"
        :footer-props="{
          itemsPerPageText: 'Itens por Página'
        }"
        no-data-text="Nenhuma Taxa Média de Depreciação disponível"
        no-results-text="Nenhum registro correspondente encontrado"
        :loading="loading"
      >
        <template v-slot:[`item.id`]="{ item }">
          {{ item.id }}
        </template>
        <template v-slot:[`item.titulo`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <div class="text-truncate-column" v-bind="attrs" v-on="on">
                {{ item.titulo }}
              </div>
            </template>
            <span>{{ item.titulo }}</span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <general-status :status="item.status" />
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            id="action-download-taxa-media-depreciacao"
            :class="item.status != 'SUCESSO' ? 'cursor-block' : ''"
            min-width="0"
            class="px-1"
            fab
            icon
            x-small
            :loading="downloadProgressData[item.id] !== undefined ? true : false"
            :disabled="item.status != 'SUCESSO'"
            @click="downloadItem(item)"
          >
            <v-icon small>mdi-download</v-icon>
          </v-btn>
          <v-btn
            id="action-edit-taxa-media-depreciacao"
            :class="!canEditItem(item) ? 'cursor-block' : ''"
            :disabled="!canEditItem(item)"
            min-width="0"
            class="ml-n1 px-1"
            fab
            icon
            x-small
            @click="$router.push(`edit/${item.id}`)"
          >
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                id="action-more-taxa-media-depreciacao"
                color="white"
                class="ml-1"
                height="22px"
                width="22px"
                fab
                x-small
                elevation="1"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon small>mdi mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item v-if="canRunItem(item)" @click="executarItem(item.id)">
                <v-list-item-title>
                  <v-icon small>mdi-play</v-icon>
                  Executar
                </v-list-item-title>
              </v-list-item>
              <v-list-item v-if="canDeleteItem(item)" @click="openDialogDelete(item.id, item.titulo)">
                <v-list-item-title>
                  <v-icon small>mdi-trash-can-outline</v-icon>
                  Deletar
                </v-list-item-title>
              </v-list-item>
              <v-list-item
                @click="((dialogCreationLog = true), (dialogCreationLogData = { username: item.usuario, created: item.created }))"
              >
                <v-list-item-title>
                  <v-icon small>mdi-table-search</v-icon>
                  Log
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
        <template v-slot:[`footer.page-text`]>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon color="primary" v-bind="attrs" v-on="on" @click="$emit('rechargeTable')">mdi-refresh</v-icon>
            </template>
            <span>Clique aqui para recarregar as taxas médias de depreciação</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </base-material-card>
    <dialog-delete
      :show-dialog="dialogDelete"
      @changeValue="dialogDelete = $event"
      @deleteTriggered="deletarItem(dialogDeleteData.id)"
    >
      <template slot="title">Deletar Taxa #{{ dialogDeleteData.id }}</template>
      <template slot="body">
        Tem certeza que deseja deletar a Taxa Média de Depreciação
        <strong>{{ dialogDeleteData.titulo }}</strong>
        ?
      </template>
    </dialog-delete>
    <dialog-creation-log :dialog.sync="dialogCreationLog" :item="dialogCreationLogData" />
  </v-container>
</template>

<script>
  import TaxaMediaDepreciacaoService from '@/services/TaxaMediaDepreciacaoService';
  import powerupsActionsMixins from '@/mixins/powerupsActionsMixins';

  export default {
    components: {
      GeneralStatus: () => import('@/components/general/GeneralStatus.vue'),
      GeneralProgressBars: () => import('@/components/general/GeneralProgressBars.vue'),
      DialogCreationLog: () => import('@/components/general/DialogCreationLog'),
      DialogDelete: () => import('@/components/general/DialogDelete')
    },
    mixins: [powerupsActionsMixins],

    props: {
      items: {
        type: Array,
        default: () => []
      },
      loading: {
        type: Boolean,
        required: true
      }
    },

    emits: ['rechargeTable'],

    data: () => ({
      powerupService: TaxaMediaDepreciacaoService,
      powerupEntityName: 'Taxa Média de Depreciação',
      search: '',
      headers: [
        {
          text: '#',
          value: 'id'
        },
        {
          text: 'Título',
          value: 'titulo'
        },
        {
          text: 'Data Base AIS',
          value: 'data_base'
        },
        {
          text: 'Executado',
          value: 'data_processamento'
        },
        {
          text: 'Status',
          value: 'status'
        },
        {
          sortable: false,
          text: 'Ações',
          value: 'actions',
          class: 'pl-4'
        }
      ],
      dialogDelete: false,
      dialogDeleteData: { id: undefined, titulo: undefined }
    })
  };
</script>

<style>
  .swithInterna label {
    font-size: 13px !important;
    margin-left: 0px !important;
  }
</style>
